import React, { createContext, useContext, useState } from "react";
import { createChecksum } from "../commonFunctions/fileFolderConfig";
import { useAppOrgContext } from "./AppOrgContext";
import axiosInstance, { handleApiError } from "../services/axiosService";
import { useTreeContext } from "./TreeDataContext";
import { getTreeDataForApiInOut } from "../utils/treeData/treeDataUtils";

const APIContextApi = createContext();
const useAPIContext = () => useContext(APIContextApi);

const APIContext = ({ children }) => {
  const [selectedApi, setSelectedApi] = useState({
    id: "",
    name: ""
  });

  // for saving api data
  const [selectedApiData, setSelectedApiData] = useState(null);

  const { setApiExplorerData } = useTreeContext();
  const { currentChecksum } = useAppOrgContext();

  const getApiConfig = () => {
    if (selectedApi.parentData === "http-request-templates") {
      const { name: _, ...updateData } = selectedApiData;
      return updateData;
    }
    return selectedApiData;
  };

  const fetchAllApiInOut = async () => {
    try {
      const [webhookRes, webhookV2Res, httpTemplatesRes, activityWebhook] = await Promise.all([
        axiosInstance.get("/webhook"),
        axiosInstance.get("/webhook-config"),
        axiosInstance.get("/http-request-templates"),
        axiosInstance.get("/activity-webhook")
      ]);

      const combinedWebhookData = [...webhookRes.data, ...webhookV2Res.data];
      const httpTemplatesData = httpTemplatesRes.data;

      const treeData = getTreeDataForApiInOut({
        webhook: combinedWebhookData,
        httpTemp: httpTemplatesData,
        activityWebhook: activityWebhook.data
      });
      setApiExplorerData(treeData);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSaveApi = async () => {
    try {
      // return;
      const config = getApiConfig();
      let toastMessage;
      if (selectedApi.parentData === "http-request-templates") {
        const { message } = await axiosInstance.put(`/http-request-templates/${selectedApi.id}`, config);
        toastMessage = message;
      } else if (selectedApi.parentData === "webhook" && selectedApi?.fileInfo?.version === "v2") {
        const newConfig = JSON.parse(JSON.stringify(config));
        if (!newConfig) return;
        const { message } = await axiosInstance.put(`/webhook-config/${selectedApi.id}`, newConfig);
        toastMessage = message;
      } else {
        const newConfig = getParseData(JSON.parse(JSON.stringify(config)));
        if (!newConfig) return;
        const { message } = await axiosInstance.patch(`/webhook/${selectedApi.id}`, newConfig);
        toastMessage = message;
      }
      const newChecksum = createChecksum(config);
      currentChecksum.current = newChecksum;
      return {
        data: config,
        toastMessage
      };
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  };

  const getParseData = (config) => {
    try {
      config.projections = JSON.parse(config.projections);
      config.body = JSON.parse(config.body);
      return config;
    } catch (error) {
      handleApiError("Please Enter Valid JSON Data");
      return null;
    }
  };

  const addDynamicExtraFolders = () => {
    const apiData = selectedApiData || {};

    if (!apiData || Object.keys(apiData).length === 0) {
      return [];
    }

    const subFolders = [];

    if (apiData.query_params && Array.isArray(apiData.query_params) && apiData.query_params.length > 0) {
      const queryParamsFolder = {
        id: "query_params",
        name: "Query Parameters",
        isFolder: true,
        isOpen: false,
        items: apiData.query_params.map((param) => ({
          id: `query_${param}`,
          name: param,
          type: "text",
          isFolder: false,
          display: param
        }))
      };
      subFolders.push(queryParamsFolder);
    }

    if (apiData.path_params && Array.isArray(apiData.path_params) && apiData.path_params.length > 0) {
      const pathParamsFolder = {
        id: "path_params",
        name: "Path Parameters",
        isFolder: true,
        isOpen: false,
        items: apiData.path_params.map((param) => ({
          id: `path_${param}`,
          name: param,
          type: "text",
          isFolder: false,
          display: param
        }))
      };
      subFolders.push(pathParamsFolder);
    }

    if (apiData.body_params && typeof apiData.body_params === "object" && !Array.isArray(apiData.body_params)) {
      const bodyParamKeys = Object.keys(apiData.body_params);
      if (bodyParamKeys.length > 0) {
        const bodyParamsFolder = {
          id: "body_params",
          name: "Body Parameters",
          isFolder: true,
          isOpen: false,
          items: bodyParamKeys.map((key) => ({
            id: `body_${key}`,
            name: key,
            type: apiData.body_params[key]?.type || "text",
            isFolder: false,
            display: key
          }))
        };
        subFolders.push(bodyParamsFolder);
      }
    }

    if (subFolders.length > 0) {
      const apiFolder = {
        id: "api_parameters",
        name: "API Parameters",
        isFolder: true,
        isOpen: false,
        items: subFolders
      };

      return [apiFolder];
    }

    return [];
  };

  const generateFieldObject = () => {
    const folders = addDynamicExtraFolders();
    const fieldObject = {};

    const extractItems = (items) => {
      items.forEach((item) => {
        if (item.isFolder && item.items) {
          extractItems(item.items);
        } else if (!item.isFolder) {
          fieldObject[item.id] = {
            id: item.id,
            name: item.name,
            type: item.type || "text",
            display: item.display || item.name
          };
        }
      });
    };

    folders.forEach((folder) => {
      if (folder.items) {
        extractItems(folder.items);
      }
    });

    return fieldObject;
  };

  return (
    <APIContextApi.Provider
      value={{
        selectedApi,
        setSelectedApi,
        selectedApiData,
        setSelectedApiData,
        fetchAllApiInOut,
        handleSaveApi,
        getApiConfig,
        addDynamicExtraFolders,
        generateFieldObject
      }}
    >
      {children}
    </APIContextApi.Provider>
  );
};

export default APIContext;
export { useAPIContext };
